import { Component, OnChanges, OnDestroy, SimpleChanges, inject, input, model, output, OnInit } from '@angular/core';
import {
  ControllableObjectDetailDataCrate,
  ControllableObjectMessageType,
  ControllableObjectType,
  ControllableObjectUpdateData,
  Item,
  MonitorItem,
  ShowInMapOrDetailModeEnum,
  SignalRService,
  UnitStatusEnum,
} from '@cue/api';

import { ButtonComponent } from '../button/button.component';

import { CmUnityLocalizedStringPipe } from '@cue/utilities';
import { Store } from '@ngrx/store';
import { AppState, currentControllableObject } from '@assist/shared/data';
import { Observable } from 'rxjs';
import {
  cueCheckCircle,
  cueExclamation,
  cueIcon,
  cueInfo,
  cueMinus,
  cueMinusCircle,
  cueMute,
  cuePlus,
  cuePlusCircle,
  cueRaindrops,
  cueScreen,
  cueSun,
  cueWarning,
  IconComponent,
} from '@cue/ui/icons';
import { Memoize } from 'typescript-memoize';
import { RepeatButtonIntervalData } from '@cue/data';
import { NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { NumericTextBoxModule, SliderComponent } from '@progress/kendo-angular-inputs';
import { TranslocoPipe } from '@jsverse/transloco';
import { TooltipDirective } from '@cue/ui/tooltips';

@Component({
  selector: 'cue-controllable-object',
  imports: [
    ButtonComponent,
    CmUnityLocalizedStringPipe,
    IconComponent,
    NgStyle,
    FormsModule,
    TimePickerModule,
    NumericTextBoxModule,
    SliderComponent,
    TranslocoPipe,
    TooltipDirective,
  ],
  templateUrl: './controllable-object.component.html',
  styleUrls: ['./controllable-object.component.scss'],
})
export class ControllableObjectComponent implements OnChanges, OnInit, OnDestroy {
  private signalRService = inject(SignalRService);
  private store = inject<Store<AppState>>(Store<AppState>);
  private readonly legacyLongNumberInputType = 'lonf-number-input-1';
  private readonly sliderActionThrottleMs = 80;
  protected readonly messageTypeEnum = ControllableObjectMessageType;
  protected readonly showInMapOrDetailModeEnum = ShowInMapOrDetailModeEnum;
  ICONS = {
    cueRaindrops,
    cueSun,
    cueExclamation,
    cueInfo,
    cueScreen,
    cueCheckCircle,
    cueMute,
    cueMinusCircle,
    cuePlusCircle,
    cueMinus,
    cuePlus,
    cueWarning,
  };

  private _startedIntervalIdForItem: RepeatButtonIntervalData[] = [];
  private pendingSliderActionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private lastSliderActionSentAt = new Map<string, number>();
  qrCodeId = input<string>();
  qrCodeListOfResource = input<string[]>();
  controllableObjectData = model<ControllableObjectDetailDataCrate>();
  unitDataNotAvailable = output<boolean>();

  currentControllableObject: Observable<any>;

  ngOnInit() {
    this.currentControllableObject = this.store.select(currentControllableObject);

    try {
      this.signalRService
        .listen<ControllableObjectUpdateData>('ObjectChanged', false, '/MessageHub')
        .subscribe((updatedData) => {
          if (updatedData && this.controllableObjectData()?.data.objectId === updatedData.objectId) {
            //nema urceno ID - updatuje se pouze label hlavniho objektu
            if (updatedData.data?.id == null && updatedData.data?.label) {
              this.controllableObjectData.update((oldData) => ({
                ...oldData,
                data: {
                  ...oldData.data,
                  label: updatedData.data.label,
                },
              }));
              return;
            }

            let dataUpdated = this.updateItemData(updatedData);
            if (dataUpdated) {
              return;
            } else if (this.controllableObjectData()?.data.group) {
              dataUpdated = this.updateGroupItemData(updatedData);
              if (dataUpdated) {
                return;
              } else if (updatedData.data.label) {
                dataUpdated = this.updateGroupLabelData(updatedData);
              } else {
                dataUpdated = this.updateGroupMessageData(updatedData);
              }
            }
            if (!dataUpdated) {
              dataUpdated = this.updateIndicatorData(updatedData);
              if (!dataUpdated) {
                this.updateMainMessage(updatedData);
              }
            }
          }
        });
    } catch (e) {
      console.error('Error during updating data...');
      console.error(e);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controllableObjectData'] && this.controllableObjectData()) {
      if (this.controllableObjectData().unitStatus === UnitStatusEnum.NotAvailable) {
        this.unitDataNotAvailable.emit(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.pendingSliderActionTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.pendingSliderActionTimeouts.clear();
    this.lastSliderActionSentAt.clear();
  }

  protected getDefaultIconAccordingStatus(statusType: ControllableObjectMessageType): cueIcon {
    switch (statusType) {
      case ControllableObjectMessageType.Success:
        return this.ICONS.cueCheckCircle;
      case ControllableObjectMessageType.Warning:
        return this.ICONS.cueWarning;
      case ControllableObjectMessageType.Error:
        return this.ICONS.cueExclamation;
      case ControllableObjectMessageType.Info:
        return this.ICONS.cueInfo;
      default:
        return null;
    }
  }

  protected performAction(item: Item) {
    console.log('performAction');
    this.signalRService.sendMessageSignalR(this.controllableObjectData().data.objectId, item.id, '/MessageHub');
  }

  protected repeatButtonStart(item: Item) {
    console.log('repeatButtonStart');
    this.performAction(item);
    const intervalId = window.setInterval(() => {
      this.performAction(item);
    }, item.repeatButtonInterval * 1000);
    this._startedIntervalIdForItem.push({
      itemId: item.id,
      startedIntervalId: intervalId,
    });
  }

  protected repeatButtonEnd(item: Item) {
    console.log('repeatButtonEnd');
    const itemIdxToDelete = this._startedIntervalIdForItem.findIndex((x) => x.itemId === item.id);
    if (itemIdxToDelete > -1) {
      clearInterval(this._startedIntervalIdForItem[itemIdxToDelete].startedIntervalId);
      this._startedIntervalIdForItem.splice(itemIdxToDelete, 1);
    }
  }

  private updateMainMessage(updatedData: ControllableObjectUpdateData) {
    if (updatedData.data.id === this.controllableObjectData().data.message?.id) {
      this.controllableObjectData.set({
        ...this.controllableObjectData(),
        data: {
          ...this.controllableObjectData().data,
          message: {
            ...this.controllableObjectData().data.message,
            ...updatedData.data,
          },
        },
      });
    }
  }

  private updateIndicatorData(updatedData: ControllableObjectUpdateData): boolean {
    if (!this.isIndicatorType(this.controllableObjectData().data.type)) {
      return false;
    }

    if (this.controllableObjectData().data.objectId === updatedData.objectId) {
      this.controllableObjectData.update((objectData) => ({
        ...objectData,
        data: {
          ...objectData.data,
          ...updatedData.data,
        },
      }));
      return true;
    }
    return false;
  }

  protected isControlType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Control;
  }

  protected isValueInputType(type?: ControllableObjectType | string): boolean {
    return this.isSliderType(type) || this.isTimeInputType(type) || this.isLongNumberInputType(type);
  }

  protected isSliderType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Slider;
  }

  protected isTimeInputType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.TimeInput;
  }

  protected isLongNumberInputType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.LongNumberInput || type === this.legacyLongNumberInputType;
  }

  protected isMonitorType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Monitor;
  }

  protected isIndicatorType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Indicator;
  }

  private updateItemData(updatedData: ControllableObjectUpdateData): boolean {
    const idxToUpdate = this.controllableObjectData().data.item?.findIndex((x) => x.id == updatedData.data?.id);

    if (idxToUpdate > -1) {
      this.controllableObjectData.update((oldData) => ({
        ...oldData,
        data: {
          ...oldData.data,
          item: oldData.data.item.map((item, index) => {
            if (index === idxToUpdate) {
              return {
                ...item,
                ...updatedData.data,
              };
            }
            return item;
          }),
        },
      }));
      return true;
    }
    return false;
  }

  private updateGroupItemData(updatedData: ControllableObjectUpdateData): boolean {
    this.controllableObjectData().data.group?.forEach((group, index) => {
      const groupItemIdx = group.item.findIndex((item) => item.id === updatedData.data.id);
      if (index > -1 && groupItemIdx > -1) {
        this.controllableObjectData.update((oldDatat) => ({
          ...oldDatat,
          data: {
            ...oldDatat.data,
            group: oldDatat.data.group.map((g, idx) => {
              if (idx === index) {
                return {
                  ...g,
                  item: g.item.map((item, itemIdx) => {
                    if (itemIdx === groupItemIdx) {
                      return {
                        ...item,
                        ...updatedData.data,
                      };
                    }
                    return item;
                  }),
                };
              }
              return g;
            }),
          },
        }));
        return true;
      }
      return false;
    });
    return false;
  }

  private updateGroupLabelData(updatedData: ControllableObjectUpdateData) {
    const groupIdxToUpdate = this.controllableObjectData().data.group?.findIndex((x) => x.id === updatedData.data.id);

    if (groupIdxToUpdate > -1) {
      this.controllableObjectData.update((oldData) => ({
        ...oldData,
        data: {
          ...oldData.data,
          group: oldData.data.group.map((group, index) => {
            if (index === groupIdxToUpdate) {
              return {
                ...group,
                label: updatedData.data.label,
              };
            }
            return group;
          }),
        },
      }));
      return true;
    }
    return false;
  }

  private updateGroupMessageData(updatedData: ControllableObjectUpdateData) {
    const groupIdxToUpdate = this.controllableObjectData().data.group?.findIndex(
      (x) => x.message.id === updatedData.data.id
    );

    if (groupIdxToUpdate > -1) {
      this.controllableObjectData.update((oldData) => ({
        ...oldData,
        data: {
          ...oldData.data,
          group: oldData.data.group.map((group, index) => {
            if (index === groupIdxToUpdate) {
              return {
                ...group,
                message: {
                  ...group.message,
                  ...updatedData.data,
                },
              };
            }
            return group;
          }),
        },
      }));
      return true;
    }
    return false;
  }

  @Memoize((item: ControllableObjectDetailDataCrate, property: ShowInMapOrDetailModeEnum) => {
    return item?.data?.objectId + ';' + property;
  })
  protected canShowHideableItemProperty(
    item: ControllableObjectDetailDataCrate,
    property: ShowInMapOrDetailModeEnum
  ): boolean {
    if (this.qrCodeListOfResource()?.length > 0) {
      return item?.showInDetail?.some(
        (x) => this.qrCodeListOfResource().findIndex((qr) => qr === x.qrCodeId) > -1 && x.showInDetail == property
      );
    } else {
      return item?.showInDetail?.some((x) => x.qrCodeId === this.qrCodeId() && x.showInDetail == property);
    }
  }

  @Memoize((item: ControllableObjectDetailDataCrate) => {
    return item?.data?.color;
  })
  protected getTextColorIfAvailable(item: ControllableObjectDetailDataCrate): string {
    if (!this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Color)) {
      return '#212529';
    }
    return this.getTextColorBasedOnBg(item.data?.color);
  }

  protected getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? '#212529' : '#ffffff';
  }

  protected canShowValueInputObjectLabel(item: ControllableObjectDetailDataCrate): boolean {
    return (
      !!item?.data?.label &&
      (this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
        this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label))
    );
  }

  protected canShowValueInput(item: ControllableObjectDetailDataCrate): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Control) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Unit) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Limits)
    );
  }

  protected hasValueInputContent(item?: MonitorItem): boolean {
    return !!item && (!!item.label || this.hasInputValue(item) || !!item.unit || this.hasInputLimits(item));
  }

  protected hasInputValue(item?: MonitorItem): boolean {
    return item?.value !== null && item?.value !== undefined && item?.value !== '';
  }

  protected hasInputLimits(item?: MonitorItem): boolean {
    return item?.minimum !== undefined || item?.maximum !== undefined;
  }

  protected getInputLimitsText(item?: MonitorItem): string {
    if (!item) {
      return '';
    }

    if (item.minimum !== undefined && item.maximum !== undefined) {
      return `${item.minimum}–${item.maximum}`;
    }

    if (item.minimum !== undefined) {
      return `≥ ${item.minimum}`;
    }

    if (item.maximum !== undefined) {
      return `≤ ${item.maximum}`;
    }

    return '';
  }

  protected getInputValue(item?: MonitorItem): string | number {
    return (item?.value as string | number) ?? '';
  }

  protected getSliderValue(item?: MonitorItem): number {
    const value = Number(item?.value ?? item?.minimum ?? 0);
    return Number.isFinite(value) ? value : 0;
  }

  protected getSliderMin(item?: MonitorItem): number {
    const min = Number(item?.minimum ?? 0);
    return Number.isFinite(min) ? min : 0;
  }

  protected getSliderMax(item?: MonitorItem): number {
    const max = Number(item?.maximum ?? 100);
    return Number.isFinite(max) ? max : 100;
  }

  protected getSliderStep(item?: MonitorItem): number {
    const step = Number(item?.step ?? 1);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  protected isSliderAtMin(item?: MonitorItem): boolean {
    return this.getSliderValue(item) <= this.getSliderMin(item);
  }

  protected isSliderAtMax(item?: MonitorItem): boolean {
    return this.getSliderValue(item) >= this.getSliderMax(item);
  }

  protected stepSliderValue(item: MonitorItem, direction: -1 | 1): void {
    const min = this.getSliderMin(item);
    const max = this.getSliderMax(item);
    const next = Math.min(max, Math.max(min, this.getSliderValue(item) + direction * this.getSliderStep(item)));
    if (next === this.getSliderValue(item)) {
      return;
    }
    this.onSliderValueChanged(item.id, next);
  }

  protected setSliderToMin(item: MonitorItem): void {
    if (this.isSliderAtMin(item)) {
      return;
    }
    this.onSliderValueChanged(item.id, this.getSliderMin(item));
  }

  protected setSliderToMax(item: MonitorItem): void {
    if (this.isSliderAtMax(item)) {
      return;
    }
    this.onSliderValueChanged(item.id, this.getSliderMax(item));
  }

  protected onSliderValueChanged(itemId: string, value: number | null): void {
    this.onValueInputChanged(itemId, `${value ?? 0}`, ControllableObjectType.Slider);
    this.scheduleSliderValueInputAction(itemId);
  }

  protected onLongNumberValueChanged(itemId: string, value: number | null): void {
    const rawValue = value == null ? '' : `${value}`;
    const normalizedValue = this.normalizeValueInputValue(rawValue, ControllableObjectType.LongNumberInput);
    const currentValue = this.getValueInputActionValue(itemId);
    const normalizedCurrentValue = this.normalizeValueInputValue(
      currentValue == null ? '' : `${currentValue}`,
      ControllableObjectType.LongNumberInput
    );

    if (normalizedValue === normalizedCurrentValue) {
      return;
    }

    this.onValueInputChanged(itemId, rawValue, ControllableObjectType.LongNumberInput);
    this.performValueInputAction(itemId);
  }

  protected stopEditableInputEvent(event: Event): void {
    event.stopPropagation();
  }

  protected onValueInputChanged(itemId: string, rawValue: string, type?: ControllableObjectType | string): void {
    const value = this.normalizeValueInputValue(rawValue, type);

    this.controllableObjectData.update((currentData) => ({
      ...currentData,
      data: {
        ...currentData.data,
        item: (currentData.data.item ?? []).map((item) =>
          item.id === itemId
            ? {
                ...item,
                value,
              }
            : item
        ),
      },
    }));
  }

  @Memoize((item?: MonitorItem) => `${item?.id ?? ''};${item?.value ?? ''}`)
  protected getTimePickerValue(item?: MonitorItem): Date | null {
    const value = item?.value;
    if (typeof value !== 'string') {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  protected commitTimePickerValue(itemId: string, value: Date | null): void {
    const formattedValue = this.formatTimePickerValue(value);
    const currentValue = this.getValueInputActionValue(itemId);
    const normalizedCurrentValue = currentValue == null ? '' : `${currentValue}`;

    if (formattedValue === normalizedCurrentValue) {
      return;
    }

    this.onValueInputChanged(itemId, formattedValue, ControllableObjectType.TimeInput);
    this.performValueInputAction(itemId);
  }

  private normalizeValueInputValue(rawValue: string, type?: ControllableObjectType | string): string | number {
    if (this.isSliderType(type)) {
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    if (this.isLongNumberInputType(type)) {
      if (rawValue === '') {
        return '';
      }

      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? Math.trunc(parsed) : rawValue;
    }

    return rawValue;
  }

  private formatTimePickerValue(value: Date | null): string {
    if (!value) {
      return '';
    }

    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private getValueInputActionValue(itemId: string): string | number | undefined {
    const value = this.controllableObjectData().data.item?.find((item) => item.id === itemId)?.value;
    return typeof value === 'string' || typeof value === 'number' ? value : undefined;
  }

  private performValueInputAction(itemId: string): void {
    this.signalRService.sendValueMessageSignalR(
      this.controllableObjectData().data.objectId,
      itemId,
      this.getValueInputActionValue(itemId),
      '/MessageHub'
    );
  }

  private scheduleSliderValueInputAction(itemId: string): void {
    if (this.pendingSliderActionTimeouts.has(itemId)) {
      return;
    }

    const now = Date.now();
    const lastSentAt = this.lastSliderActionSentAt.get(itemId) ?? 0;
    const elapsed = now - lastSentAt;

    if (elapsed >= this.sliderActionThrottleMs) {
      this.lastSliderActionSentAt.set(itemId, now);
      this.performValueInputAction(itemId);
      return;
    }

    const timeoutId = setTimeout(() => {
      this.pendingSliderActionTimeouts.delete(itemId);
      this.lastSliderActionSentAt.set(itemId, Date.now());
      this.performValueInputAction(itemId);
    }, this.sliderActionThrottleMs - elapsed);

    this.pendingSliderActionTimeouts.set(itemId, timeoutId);
  }
}
